import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Printer, HelpCircle, FileText, MapPin, Menu, X, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  const activeClass = (path) => location.pathname === path ? 'text-red-700 font-semibold' : 'text-slate-700 hover:text-red-700';

  const popularServices = [
    { name: 'Print PDF Online', slug: '/print-pdf-online' },
    { name: 'Print Documents Online', slug: '/print-documents-online' },
    { name: 'Online Xerox', slug: '/online-xerox' },
    { name: 'Black & White Printing', slug: '/black-and-white-printing' },
    { name: 'Color Printing', slug: '/color-printing' },
    { name: 'Spiral Binding', slug: '/spiral-binding' },
    { name: 'Project Report Printing', slug: '/project-report-printing' },
    { name: 'Thesis Printing', slug: '/thesis-printing' },
    { name: 'Express Kiosk Print', slug: '/express-printing' }
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group focus:outline-hidden focus:ring-2 focus:ring-red-700 rounded-lg p-1">
            <img 
              src="/inchpaper-printezy.png" 
              alt="Inchpaper Print Logo" 
              className="h-9 sm:h-10 w-auto object-contain"
              onError={(e) => { e.target.src = '/inchpaper-logo.png'; }}
            />
          </Link>

          {/* Desktop Nav Items */}
          <nav aria-label="Main Navigation" className="hidden lg:flex items-center gap-5 text-sm font-medium">
            <Link to="/" className={`transition-colors ${activeClass('/')}`}>
              Home
            </Link>

            <Link to="/printing-services" className={`transition-colors ${activeClass('/printing-services')}`}>
              Services
            </Link>

            <Link to="/printing-prices" className={`transition-colors ${activeClass('/printing-prices')}`}>
              Pricing
            </Link>

            <Link to="/print-centers" className={`transition-colors ${activeClass('/print-centers')}`}>
              Print Centers
            </Link>

            <Link to="/blog" className={`transition-colors ${activeClass('/blog')}`}>
              Blog
            </Link>

            <Link to="/faq" className={`transition-colors ${activeClass('/faq')}`}>
              FAQs
            </Link>

            <Link to="/contact-us" className={`transition-colors ${activeClass('/contact-us')}`}>
              Contact
            </Link>
          </nav>

          {/* Right Action Button */}
          <div className="hidden sm:flex items-center gap-3">
            <Link to="/#upload">
              <Button 
                className="bg-[#7d0909] hover:bg-[#5e0707] text-white font-medium text-sm rounded-full px-5 shadow-xs transition-all flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                <span>Upload & Print</span>
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex lg:hidden items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-slate-700 hover:text-slate-900 rounded-lg focus:outline-hidden"
              aria-label="Toggle Navigation Menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 space-y-2 animate-in slide-in-from-top duration-200">
          <Link 
            to="/" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            Home
          </Link>

          <Link 
            to="/printing-services" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            Services
          </Link>

          <Link 
            to="/printing-prices" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            Pricing
          </Link>

          <Link 
            to="/print-centers" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            Print Centers
          </Link>

          <Link 
            to="/blog" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            Blog
          </Link>

          <Link 
            to="/faq" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            FAQs
          </Link>

          <Link 
            to="/contact-us" 
            onClick={() => setIsOpen(false)} 
            className="block py-2 text-base font-medium text-slate-800 hover:text-red-700 border-b border-slate-100"
          >
            Contact
          </Link>

          <div className="pt-2">
            <Link to="/#upload" onClick={() => setIsOpen(false)}>
              <Button className="w-full bg-[#7d0909] hover:bg-[#5e0707] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2">
                <Printer className="w-5 h-5" />
                <span>Start Printing Now</span>
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
